/**
 * New Year pixel art — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_seasons`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const newYearPixelArtPack: ToilePack = {
    slug: 'new-year-pixel-art',
    category: 'season',
    name: 'New Year pixel art',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'firework', label: 'Pixel firework', subject: 'A firework burst drawn in crisp pixel art about 32 pixels wide, trailing sparks falling below it, magnified large'},
        {key: 'clock', label: 'Pixel clock', subject: 'A round clock face at midnight in pixel art about 24 pixels tall, both hands pointed straight up, magnified large'},
        {key: 'skyline', label: 'Pixel skyline', subject: 'A pixel art city skyline about 64 pixels wide with fireworks bursting above the rooftops, magnified large'},
    ],
    styles: [
        {key: 'deco', label: 'Art deco', style: 'art deco illustration, geometric fans and sunbursts, bold clean shapes, no outlines, hard edges', palette: 'Black and gold with a little cream', framing: 'Centered on a solid black background with an even margin'},
        {key: 'neon', label: 'Neon party', style: 'neon party graphic, glowing lines, confetti and streamers, smooth gradient-free fills', palette: 'Electric pink, cyan and gold on deep navy', framing: 'Centered on a dark background, built to read clean at small size'},
        {key: 'minimal', label: 'Minimal', style: 'modern minimal illustration, few flat shapes, generous space, no outlines, hard edges', palette: 'Gold and white with a single black', framing: 'Centered with generous white space around it on a soft cream background'},
    ],
    directions: ['a rocket ship instead', 'in warm sunset colors', 'as a nighttime countdown scene'],
    directionHint: 'try: a rocket ship instead',
    seo: {
        title: 'New Year Pixel Art: Firework, Clock, Skyline',
        metaDescription: 'Nine new year pixel art pieces — 8-bit firework, pixel clock, pixel skyline — crisp square pixels, no anti-aliasing, three styles.',
        h1: 'New year pixel art: firework, clock, skyline',
        tagline: 'Nine pieces, three midnight subjects, three styles each.',
        intro: '8-bit midnight scenes built from crisp square pixels: a pixel firework, a pixel clock, a pixel skyline, magnified large with no smoothing. Ask for a pixel confetti burst, a retro arcade frame, or your own subject, and the same three styles carry it.',
        useCases: [
            'a retro game-style social post',
            'a pixel art print or poster',
            'a Twitch overlay or stream graphic',
            'a phone or desktop wallpaper',
        ],
        faq: [
            {q: 'What does this pack actually look like?', a: 'Nine 8-bit scenes — a pixel firework, a pixel clock, a pixel skyline — crisp square pixels with no anti-aliasing, in three styles.'},
            {q: 'Is this real retro game sprite art or an original design?', a: "It's an original pixel-style design, not a sprite pulled from any specific game."},
            {q: 'Will the pixels stay sharp if I resize the image?', a: 'Scaling up by whole numbers keeps the crisp block edges; scaling down or to a non-whole factor can soften them, so resize with care.'},
            {q: 'Can I get a different scene, like an arcade frame?', a: 'Yes — the direction field takes any new year subject, including a retro arcade frame or a pixel confetti burst.'},
            {q: "Can I use this in a game or product I'm selling?", a: 'Yes — check the license terms on your account for the specifics of commercial use.'},
        ],
    },
}
